<script setup lang="ts">
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const DISMISS_KEY = 'tradel_pwa_install_dismissed_at';
const DISMISS_DAYS = 7;
const SHOW_DELAY_MS = 1200;

const { $pwa } = useNuxtApp();
const { isMobileOrTablet } = useDevice();

const open = ref(false);
const deferredPrompt = shallowRef<BeforeInstallPromptEvent | null>(null);
const installing = ref(false);

type IOSBrowser = 'safari' | 'chrome' | 'firefox' | 'brave' | 'edge';

const detectIOSBrowser = (): IOSBrowser | null => {
  if (!import.meta.client) return null;
  const ua = navigator.userAgent;
  if (!/iP(hone|od|ad)/.test(ua)) return null;
  if (/EdgiOS/.test(ua)) return 'edge';
  if (/FxiOS/.test(ua)) return 'firefox';
  if (/CriOS/.test(ua)) return 'chrome';
  const nav = navigator as Navigator & { brave?: { isBrave?: () => Promise<boolean> } };
  if (nav.brave) return 'brave';
  if (/WebKit/.test(ua)) return 'safari';
  return null;
};

const iosBrowser = computed(() => detectIOSBrowser());
const isIOSBrowser = computed(() => iosBrowser.value !== null);

const browserName = computed((): string => {
  if (!import.meta.client) return '';
  const ua = navigator.userAgent;
  switch (iosBrowser.value) {
    case 'safari': return 'Safari';
    case 'chrome': return 'Chrome';
    case 'firefox': return 'Firefox';
    case 'brave': return 'Brave';
    case 'edge': return 'Edge';
    default: break;
  }
  if (/Edg\//.test(ua)) return 'Edge';
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';
  if (/Chrome/.test(ua)) return 'Chrome';
  return 'your browser';
});

const platformLabel = computed(() =>
  isIOSBrowser.value ? `${browserName.value} on iPhone or iPad` : browserName.value,
);

type StepPart = string | { icon: string };

// mdi-export-variant matches the iOS share sheet icon (box with upward arrow)
const SHARE_ICON: StepPart = { icon: 'mdi-export-variant' };
const MENU_ICON: StepPart = { icon: 'mdi-menu' };
const DOTS_ICON: StepPart = { icon: 'mdi-dots-horizontal' };

const iosSteps = computed((): StepPart[][] => {
  switch (iosBrowser.value) {
    case 'chrome':
      return [
        ['Tap ', SHARE_ICON, ' at the top right of the address bar.'],
        ['Scroll down and tap "Add to Home Screen".'],
        ['Tap "Add" to confirm.'],
      ];
    case 'firefox':
      return [
        ['Tap ', SHARE_ICON, ' to the left of the search bar.'],
        ['Scroll down and tap "Add to Home Screen".'],
        ['Tap "Add" to confirm.'],
      ];
    case 'brave':
      return [
        ['Tap ', SHARE_ICON, ' at the bottom left.'],
        ['Scroll down and tap "Add to Home Screen".'],
        ['Tap "Add" to confirm.'],
      ];
    case 'edge':
      return [
        ['Tap ', MENU_ICON, ' at the bottom right.'],
        ['Tap ', SHARE_ICON, ' Share.'],
        ['Tap "Add to Home Screen".'],
        ['Tap "Add" to confirm.'],
      ];
    default:
      return [
        ['Tap ', DOTS_ICON, ' at the bottom of the screen.'],
        ['Tap ', SHARE_ICON, ' Share.'],
        ['Tap "Add to Home Screen".'],
        ['Tap "Add" to confirm.'],
      ];
  }
});

const isStandalone = (): boolean => {
  if (!import.meta.client) return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || Boolean(nav.standalone);
};

const dismissedRecently = (): boolean => {
  const dismissedAt = window.localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  const elapsedDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
  return Number.isFinite(elapsedDays) && elapsedDays < DISMISS_DAYS;
};

const shouldShow = (): boolean => {
  if (!import.meta.client) return false;
  if (!isMobileOrTablet) return false;
  if (isStandalone()) return false;
  if (dismissedRecently()) return false;
  return isIOSBrowser.value || Boolean(deferredPrompt.value) || Boolean($pwa?.showInstallPrompt);
};

const showWhenReady = () => {
  window.setTimeout(() => {
    if (shouldShow()) open.value = true;
  }, SHOW_DELAY_MS);
};

const install = async () => {
  if (isIOSBrowser.value) return;

  installing.value = true;
  try {
    if (deferredPrompt.value) {
      await deferredPrompt.value.prompt();
      await deferredPrompt.value.userChoice;
      deferredPrompt.value = null;
    } else if ($pwa?.install) {
      await $pwa.install();
    }
    open.value = false;
  } finally {
    installing.value = false;
  }
};

const dismiss = () => {
  open.value = false;
  window.localStorage.setItem(DISMISS_KEY, Date.now().toString());
};

onMounted(() => {
  if (!isMobileOrTablet || isStandalone() || dismissedRecently()) return;

  const onBeforeInstallPrompt = (event: Event) => {
    event.preventDefault();
    deferredPrompt.value = event as BeforeInstallPromptEvent;
    if (shouldShow()) open.value = true;
  };

  const onAppInstalled = () => {
    open.value = false;
    deferredPrompt.value = null;
    window.localStorage.removeItem(DISMISS_KEY);
  };

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);

  if (isIOSBrowser.value) {
    showWhenReady();
  } else {
    watch(
      () => $pwa?.showInstallPrompt,
      (canInstall) => {
        if (canInstall && shouldShow()) open.value = true;
      },
      { immediate: true },
    );
  }

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
  });
});
</script>

<template>
  <Teleport to="body">
    <Transition name="pwa-install-fade">
      <div v-if="open" class="pwa-install" role="presentation" @click.self="dismiss">
        <section
          class="pwa-install__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-install-title"
        >
          <button class="pwa-install__close" type="button" aria-label="Close" @click="dismiss">
            <v-icon icon="mdi-close" size="20" />
          </button>

          <div class="pwa-install__icon">
            <img src="/pwa-192x192.png" alt="" />
          </div>

          <div class="pwa-install__copy">
            <p class="pwa-install__eyebrow">{{ platformLabel }}</p>
            <h2 id="pwa-install-title" class="pwa-install__title">Add Tradel to your Home Screen</h2>
            <p class="pwa-install__text">
              Open Tradel faster, keep it full screen, and make the merchant order board easier to launch.
            </p>
          </div>

          <ol v-if="isIOSBrowser" class="pwa-install__steps">
            <li v-for="(step, i) in iosSteps" :key="i">
              <template v-for="(part, j) in step" :key="j">
                <v-icon v-if="typeof part === 'object'" :icon="part.icon" size="16" class="pwa-install__step-icon" />
                <span v-else>{{ part }}</span>
              </template>
            </li>
          </ol>

          <div v-else class="pwa-install__actions">
            <button class="pwa-install__primary" type="button" :disabled="installing" @click="install">
              {{ installing ? 'Opening...' : 'Add to Home Screen' }}
            </button>
          </div>

          <button class="pwa-install__secondary" type="button" @click="dismiss">Not now</button>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.pwa-install {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 18px;
  background: rgba(0, 0, 0, 0.5);
}

.pwa-install__dialog {
  position: relative;
  width: min(100%, 420px);
  padding: 24px;
  border-radius: 18px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.pwa-install__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface));
  font-size: 20px;
  line-height: 1;
}

.pwa-install__icon {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  overflow: hidden;
  border-radius: 16px;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.pwa-install__copy {
  padding-right: 28px;
}

.pwa-install__eyebrow {
  margin: 0 0 6px;
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.pwa-install__title {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
}

.pwa-install__text {
  margin: 10px 0 0;
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-size: 15px;
  line-height: 1.45;
}

.pwa-install__steps {
  display: grid;
  gap: 8px;
  margin: 18px 0 0;
  padding-left: 22px;
  color: rgba(var(--v-theme-on-surface), 0.84);
  font-size: 15px;
  line-height: 1.4;
}

.pwa-install__step-icon {
  vertical-align: middle;
  margin-block: -2px;
  opacity: 0.9;
}

.pwa-install__actions {
  margin-top: 20px;
}

.pwa-install__primary,
.pwa-install__secondary {
  width: 100%;
  min-height: 48px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
}

.pwa-install__primary {
  border: 0;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));

  &:disabled {
    opacity: 0.65;
  }
}

.pwa-install__secondary {
  margin-top: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.pwa-install-fade-enter-active,
.pwa-install-fade-leave-active {
  transition: opacity 0.18s ease;

  .pwa-install__dialog {
    transition: transform 0.18s ease;
  }
}

.pwa-install-fade-enter-from,
.pwa-install-fade-leave-to {
  opacity: 0;

  .pwa-install__dialog {
    transform: translateY(16px);
  }
}

@media (min-width: 640px) {
  .pwa-install {
    align-items: center;
  }
}
</style>
